import { Component, ErrorInfo, ReactNode } from 'react'

interface ICanvasErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ICanvasErrorBoundaryState {
  hasError: boolean
}

/**
 * Catches errors thrown by the threejs canvas (e.g. WebGL being unavailable
 * because hardware acceleration is disabled or the GPU is blocklisted) so a
 * failing 3D section degrades to the fallback instead of unmounting the
 * entire page tree.
 */
class CanvasErrorBoundary extends Component<
  ICanvasErrorBoundaryProps,
  ICanvasErrorBoundaryState
> {
  state: ICanvasErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ICanvasErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('3D canvas failed to render, showing fallback:', error, errorInfo)
  }

  render() {
    const { children, fallback = null } = this.props
    const { hasError } = this.state

    if (hasError) return <>{fallback}</>

    return <>{children}</>
  }
}

export default CanvasErrorBoundary
